import { html, css, LitElement } from 'lit-element';
import { db } from './intrivity_app.js';
import { doc, getDoc, updateDoc, deleteField } from "firebase/firestore";

import '@material/mwc-button';
import { t, Localized, LOCALES } from './i18n.js';
import { EMPTY_TEMPLATE, hasContent, templateLanguages, getTemplateLanguage, buildTemplate } from './invitation_templates.js';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-textarea';

// Shared editor for a client's or group's invitation_template override.
// Used from both intrivity-admin-clients.js (collection="clients") and
// intrivity-admin-groups.js (collection="groups"). Resolution order at
// send time (see functions/groups.js): group's own template, then its
// client's, then the system default templates/invitation doc.
export class IntrivityInvitationTemplateDialog extends Localized(LitElement) {
  static get properties() {
    return {
      open: { type: Boolean },
      target: { type: Object },         // the client or group doc being edited
      collection: { type: String },     // "clients" or "groups"
      label: { type: String },          // "Client" or "Group" -- for copy
      parentTemplate: { type: Object }, // e.g. the group's client's invitation_template, if any
      _formData: { type: Object, state: true },
      _isLoading: { type: Boolean, state: true },
      _seedSource: { type: String, state: true }, // 'own' | 'parent' | 'default' | 'blank'
      _htmlSourceMode: { type: Boolean, state: true },
      _lang: { type: String, state: true },       // language being edited
    };
  }

  constructor() {
    super();
    this.open = false;
    this.target = null;
    this.collection = 'clients';
    this.label = 'Client';
    this.parentTemplate = null;
    this._formData = { ...EMPTY_TEMPLATE };
    this._isLoading = false;
    this._seedSource = 'own';
    this._htmlSourceMode = false;
    this._lang = 'en';
    // Per language: the copy being edited, what it was seeded with, and
    // where that seed came from ('own' | 'parent' | 'default' | 'blank').
    this._byLanguage = {};
    this._seeded = {};
    this._seedSources = {};
  }

  updated(changedProps) {
    // Re-seed the form fresh each time the dialog opens, rather than trying
    // to diff against whatever was last typed.
    if (changedProps.has('open') && this.open) {
      this._htmlSourceMode = false;
      this._seedForm();
    }
  }

  // Own override wins; otherwise pre-fill from the parent level's template
  // (the client's, for a group) so there's a real starting point to edit
  // instead of a blank form; otherwise the system default; otherwise blank.
  // Saving as-is turns whichever one was shown into this target's own
  // explicit override -- it's a starting point, not a live inheritance link.
  //
  // Each language is seeded on its own the same way.
  async _seedForm() {
    const own = this.target && this.target.invitation_template;
    const defaultTemplate = await this._loadDefaultTemplate();
    const candidates = [['own', own], ['parent', this.parentTemplate], ['default', defaultTemplate]];
    this._byLanguage = {};
    this._seeded = {};
    this._seedSources = {};
    for (const lang of templateLanguages()) {
      const found = candidates.find(([, template]) => getTemplateLanguage(template, lang));
      const seeded = found ? getTemplateLanguage(found[1], lang) : { ...EMPTY_TEMPLATE };
      this._seeded[lang] = seeded;
      this._byLanguage[lang] = { ...seeded };
      this._seedSources[lang] = found ? found[0] : 'blank';
    }
    this._lang = 'en';
    this._formData = { ...this._byLanguage.en };
    this._seedSource = this._seedSources.en;
    await this._syncEditableFromFormData();
  }

  async _switchLanguage(lang) {
    this._byLanguage[this._lang] = { ...this._formData };
    this._lang = lang;
    this._formData = { ...this._byLanguage[lang] };
    this._seedSource = this._seedSources[lang];
    await this._syncEditableFromFormData();
  }

  // What saving writes. English keeps its original behaviour: whatever is
  // shown becomes this target's own copy. Another language is kept only if
  // it was already this target's own or has been edited here -- otherwise
  // opening the dialog would silently copy every default translation into
  // an override and freeze it.
  _templateToSave() {
    this._byLanguage[this._lang] = { ...this._formData };
    const keep = {};
    for (const lang of templateLanguages()) {
      const current = this._byLanguage[lang];
      const edited = JSON.stringify(current) !== JSON.stringify(this._seeded[lang]);
      if (lang === 'en' || this._seedSources[lang] === 'own' || edited) keep[lang] = current;
    }
    return buildTemplate(keep);
  }

  // The rich-text area's content lives in the DOM, not a Lit template
  // binding (see render()) -- rebinding .innerHTML reactively on every
  // keystroke would reset the cursor/selection and undo history. So it's
  // only ever pushed in imperatively: on seed, and when switching back from
  // source mode.
  async _syncEditableFromFormData() {
    await this.updateComplete;
    const el = this.shadowRoot.getElementById('html-editable');
    if (el) el.innerHTML = this._formData.html || '';
  }

  async _loadDefaultTemplate() {
    try {
      const snap = await getDoc(doc(db, 'templates', 'invitation'));
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      console.warn('Could not load default invitation template:', e);
      return null;
    }
  }

  _handleInput(e) {
    const { name, value } = e.target;
    this._formData = { ...this._formData, [name]: value };
  }

  _handleEditableInput(e) {
    this._formData = { ...this._formData, html: e.currentTarget.innerHTML };
  }

  _execCommand(command, value = null) {
    const el = this.shadowRoot.getElementById('html-editable');
    if (!el) return;
    el.focus();
    if (command === 'createLink') {
      const url = prompt(t('editor.linkPrompt'), 'https://');
      if (!url) return;
      value = url;
    }
    document.execCommand(command, false, value);
    this._formData = { ...this._formData, html: el.innerHTML };
  }

  async _toggleHtmlSourceMode() {
    this._htmlSourceMode = !this._htmlSourceMode;
    if (!this._htmlSourceMode) {
      // Coming back from source mode -- push whatever was typed there into
      // the rich-text view.
      await this._syncEditableFromFormData();
    }
  }

  // Clears the language on screen; saving then drops that language from the
  // override (and the whole override once no language is left).
  _clear() {
    this._formData = { ...EMPTY_TEMPLATE };
    this._seedSource = 'blank';
    this._seedSources[this._lang] = 'blank';
    this._syncEditableFromFormData();
  }



  async _save() {
    if (!this.target || !this.target.uuid) return;
    this._isLoading = true;
    try {
      const ref = doc(db, this.collection, this.target.uuid);
      const template = this._templateToSave();
      if (hasContent(template) || template.translations) {
        await updateDoc(ref, { invitation_template: template });
        this.target.invitation_template = template;
      } else {
        await updateDoc(ref, { invitation_template: deleteField() });
        delete this.target.invitation_template;
      }
      this._close();
    } catch (e) {
      console.error('Error saving invitation template:', e);
      alert(t('templateDialog.saveError', { message: e.message || e }));
    } finally {
      this._isLoading = false;
    }
  }

  _close() {
    this.dispatchEvent(new CustomEvent('dialog-closed', { bubbles: true, composed: true }));
  }

  static get styles() {
    return css`
      mwc-textfield, mwc-textarea {
        width: 100%;
        margin-top: 8px;
      }
      .hint {
        font-size: 0.85em;
        color: #666;
        margin-top: 0;
      }
      .html-field-label {
        font-size: 0.75em;
        color: #666;
        margin-top: 8px;
      }
      .html-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 4px;
        padding: 4px;
        background: #f5f5f5;
        border: 1px solid #ccc;
        border-bottom: none;
      }
      .html-toolbar button {
        min-width: 28px;
        padding: 4px 8px;
        border: 1px solid #ccc;
        background: white;
        border-radius: 3px;
        cursor: pointer;
        font-size: 0.85em;
      }
      .html-toolbar button:hover {
        background: #eee;
      }
      .html-toolbar .spacer {
        flex: 1 1 auto;
      }
      #html-editable {
        width: 100%;
        min-height: 200px;
        max-height: 400px;
        overflow-y: auto;
        box-sizing: border-box;
        padding: 12px;
        border: 1px solid #ccc;
        font-family: inherit;
        font-size: 1em;
      }
      #html-editable:focus {
        outline: 2px solid #6200ee;
        outline-offset: -1px;
      }
      .html-source {
        width: 100%;
        box-sizing: border-box;
        font-family: monospace;
        font-size: 0.85em;
      }
    `;
  }

  // 'group' or 'client'. Taken from `collection`, not `label`: the label is
  // translated, so it can't be compared against.
  get _kind() {
    return this.collection === 'groups' ? 'group' : 'client';
  }

  get _seedSourceMessage() {
    switch (this._seedSource) {
      case 'own':
        return t(`templateDialog.${this._kind}.own`);
      case 'parent':
        return t('templateDialog.group.parent');
      case 'default':
        return t(`templateDialog.${this._kind}.default`);
      default:
        return t('templateDialog.noSeed');
    }
  }

  render() {
    return html`
      <mwc-dialog
        heading="${this.target ? t('templateDialog.headingFor', { name: this.target.name }) : t('templateDialog.heading')}"
        ?open=${this.open}
        @closed=${this._close}>
        <div style="min-width: 480px; max-width: 640px;">
          <p class="hint">
            ${t(`templateDialog.${this._kind}.overrides`)}
            ${t('templateDialog.clearHint')}
          </p>
          <label style="display: block; margin-top: 8px;">
            ${t('invitationLanguage.editing')}
            <select @change=${(e) => this._switchLanguage(e.target.value)}>
              ${templateLanguages().map((code) => html`
                <option value=${code} ?selected=${code === this._lang}>${LOCALES[code].name}</option>
              `)}
            </select>
          </label>
          <p class="hint" style="font-style: italic;">${this._seedSourceMessage}</p>
          <p class="hint">
            ${t('editor.placeholders')} [[client_name]], [[client_institution]], [[client_address]], [[client_email]],
            [[group_name]], [[group_description]], [[email]], [[code]], [[first]], [[last]], [[link]].
          </p>
          <mwc-textfield
            label=${t('editor.subject')}
            name="subject"
            .value=${this._formData.subject}
            @input=${this._handleInput}
            ?disabled=${this._isLoading}>
          </mwc-textfield>
          <div class="html-field-label" id="html-field-label">${t('editor.htmlTemplate')}</div>
          <div class="html-toolbar" role="toolbar" aria-label=${t('editor.toolbar')}>
            <button type="button" title=${t('editor.bold')} aria-label=${t('editor.bold')} style="font-weight:bold;" @click=${() => this._execCommand('bold')}>B</button>
            <button type="button" title=${t('editor.italic')} aria-label=${t('editor.italic')} style="font-style:italic;" @click=${() => this._execCommand('italic')}>I</button>
            <button type="button" title=${t('editor.underline')} aria-label=${t('editor.underline')} style="text-decoration:underline;" @click=${() => this._execCommand('underline')}>U</button>
            <button type="button" title=${t('editor.bulleted')} aria-label=${t('editor.bulleted')} @click=${() => this._execCommand('insertUnorderedList')}>• ${t('editor.list')}</button>
            <button type="button" title=${t('editor.numbered')} aria-label=${t('editor.numbered')} @click=${() => this._execCommand('insertOrderedList')}>1. ${t('editor.list')}</button>
            <button type="button" title=${t('editor.link')} aria-label=${t('editor.insertLink')} @click=${() => this._execCommand('createLink')}>${t('editor.link')}</button>
            <button type="button" title=${t('editor.removeLink')} aria-label=${t('editor.removeLink')} @click=${() => this._execCommand('unlink')}>${t('editor.unlink')}</button>
            <button type="button" title=${t('editor.clearFormatting')} aria-label=${t('editor.clearFormatting')} @click=${() => this._execCommand('removeFormat')}>${t('editor.clear')}</button>
            <span class="spacer"></span>
            <button type="button" @click=${this._toggleHtmlSourceMode}>${this._htmlSourceMode ? t('editor.visual') : t('editor.source')}</button>
          </div>
          ${this._htmlSourceMode ? html`
            <mwc-textarea
              class="html-source"
              rows="10"
              name="html"
              .value=${this._formData.html}
              @input=${this._handleInput}
              ?disabled=${this._isLoading}>
            </mwc-textarea>
          ` : html`
            <div
              id="html-editable"
              contenteditable
              role="textbox"
              aria-multiline="true"
              aria-labelledby="html-field-label"
              @input=${this._handleEditableInput}>
            </div>
          `}
          <mwc-textarea
            rows="8"
            label=${t('editor.textTemplate')}
            name="text"
            .value=${this._formData.text}
            @input=${this._handleInput}
            ?disabled=${this._isLoading}>
          </mwc-textarea>
          <mwc-button outlined label=${t('templateDialog.clearUseDefault')} @click=${this._clear} ?disabled=${this._isLoading}></mwc-button>
        </div>
        <mwc-button slot="secondaryAction" dialogAction="cancel" label=${t('common.cancel')} ?disabled=${this._isLoading}></mwc-button>
        <mwc-button slot="primaryAction" raised label="${this._isLoading ? t('common.saving') : t('common.save')}" @click=${this._save} ?disabled=${this._isLoading}></mwc-button>
      </mwc-dialog>
    `;
  }
}
if (!customElements.get('intrivity-invitation-template-dialog')) customElements.define('intrivity-invitation-template-dialog', IntrivityInvitationTemplateDialog);
